import React, { useState } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  Container, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Divider,
  Button 
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Instagram as InstagramIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

const AboutPage = () => {
  const { t, isArabic } = useLanguage();
  const [expandedFAQ, setExpandedFAQ] = useState(false);

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const faqData = [
    {
      id: 'faq1',
      question: t('I can\'t find my lecturer. What should I do?', 'لا أستطيع العثور على محاضري. ماذا أفعل؟'),
      answer: t(
        'If you can\'t find your lecturer, try the following steps:\n\n1. Search using both English and Arabic names - lecturers may be listed under different name formats\n2. Try searching with just the first name or last name\n\nStill can\'t find them? Contact us on Instagram and we\'ll add them for you!',
        'إذا لم تتمكن من العثور على محاضرك، جرب الخطوات التالية:\n\n1. ابحث باستخدام الأسماء الإنجليزية والعربية - قد يكون المحاضرون مُدرَجين بصيغ أسماء مختلفة\n2. جرب البحث بالاسم الأول أو الأخير فقط\n\nما زلت غير قادرًا على العثور عليه؟ تواصل معنا على إنستقرام وسنضيفه لك!'
      )
    },
    {
      id: 'faq2',
      question: t('How do I leave a review for a lecturer?', 'كيف أترك تقييمًا لمحاضر؟'),
      answer: t(
        'To leave a review:\n\n1. Search for your lecturer using the search function\n2. Click on their name to go to their profile page\n3. Scroll down to the "Add Review" section\n4. Rate them on the different criteria (1-5 stars)\n5. Write your detailed review\n6. Submit your review\n\nYour review will help other students make informed decisions.',
        'لترك تقييم:\n\n1. ابحث عن محاضرك باستخدام أداة البحث\n2. انقر على اسمه للانتقال إلى صفحة ملفه الشخصي\n3. انتقل لأسفل إلى قسم "إضافة تقييم"\n4. قيمه على المعايير المختلفة (1-5 نجوم)\n5. اكتب تقييمك التفصيلي\n6. أرسل تقييمك\n\nسيساعد تقييمك الطلاب الآخرين على اتخاذ قرارات مدروسة.'
      )
    },
    {
      id: 'faq3',
      question: t('Is my review anonymous?', 'هل تقييمي مجهول؟'),
      answer: t(
        'Yes, all reviews on Me\'ar are completely anonymous. We do not collect or display any personal information that could identify the reviewer. Your privacy and safety are our top priorities.',
        'نعم، جميع التقييمات على معيار مجهولة تمامًا. نحن لا نجمع أو نعرض أي معلومات شخصية يمكن أن تحدد هوية المقيم. خصوصيتك وأمانك هما أولويتنا القصوى.'
      )
    },
    {
      id: 'faq4',
      question: t('Can lecturers see who reviewed them?', 'هل يمكن للمحاضرين رؤية من قيمهم؟'),
      answer: t(
        'No, lecturers cannot see who reviewed them. All reviews are anonymous. This ensures honest and unbiased feedback.',
        'لا، لا يمكن للمحاضرين رؤية من قيمهم. جميع التقييمات مجهولة. هذا يضمن تقييماً صادقاً وغير متحيز.'
      )
    },
    {
      id: 'faq5',
      question: t('How can I request a lecturer to be added?', 'كيف يمكنني طلب إضافة محاضر؟'),
      answer: t(
        'If you can\'t find a lecturer in our database, you can contact us at saudimear@gmail.com with the following information:\n\n- Lecturer\'s full name (in both English and Arabic if available)\n- University name\n- Department/College\n- Any additional details\n\nWe will review and add them to our database as soon as possible.',
        'إذا لم تتمكن من العثور على محاضر في قاعدة بياناتنا، يمكنك الاتصال بنا على saudimear@gmail.com مع المعلومات التالية:\n\n- الاسم الكامل للمحاضر (باللغتين الإنجليزية والعربية إن أمكن)\n- اسم الجامعة\n- القسم/الكلية\n- أي تفاصيل إضافية\n\nسنراجع الطلب ونضيف المحاضر إلى قاعدة بياناتنا في أقرب وقت ممكن.'
      )
    }
  ];

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
        <Typography variant="h4" gutterBottom color="primary">
          {t('About Me\'ar', 'حول معيار')}
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('Our Mission', 'مهمتنا')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t(
              'Me\'ar is dedicated to helping students in Saudi Arabia make informed decisions about their education by providing a platform to share and discover lecturer reviews and ratings.',
              'معيار مخصص لمساعدة الطلاب في المملكة العربية السعودية على اتخاذ قرارات مدروسة حول تعليمهم من خلال توفير منصة لمشاركة واكتشاف تقييمات ومراجعات المحاضرين.'
            )}
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            {t('How It Works', 'كيف يعمل')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t(
              'Students can search for lecturers by university and department, read detailed reviews from other students, and contribute their own experiences to help the academic community.',
              'يمكن للطلاب البحث عن المحاضرين حسب الجامعة والقسم، وقراءة المراجعات التفصيلية من الطلاب الآخرين، والمساهمة بتجاربهم الخاصة لمساعدة المجتمع الأكاديمي.'
            )}
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            {t('Rating System', 'نظام التقييم')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t(
              'Our comprehensive rating system evaluates lecturers across multiple criteria including:',
              'نظام التقييم الشامل لدينا يقيم المحاضرين عبر معايير متعددة تشمل:'
            )}
          </Typography>
          <Box component="ul" sx={{ ml: 2 }}>
            <Typography component="li" variant="body1">
              {t('Teaching Quality & Delivery', 'جودة التدريس والإلقاء')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('Knowledge & Expertise', 'المعرفة والخبرة')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('Communication & Accessibility', 'التواصل وسهولة الوصول إلى المحاضر')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('Assessment & Feedback', 'التقييم والتغذية الراجعة')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('Student Engagement & Support', 'تفاعل الطلاب ودعمهم')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('Professional Conduct', 'السلوك المهني')}
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* FAQ Section */}
          <Typography variant="h5" gutterBottom color="primary" sx={{ textAlign: isArabic ? 'right' : 'left' }}>
            {t('Frequently Asked Questions', 'الأسئلة الشائعة')}
          </Typography>
          
          <Box sx={{ mt: 3, direction: isArabic ? 'rtl' : 'ltr' }}>
            {faqData.map((faq) => (
              <Accordion 
                key={faq.id}
                expanded={expandedFAQ === faq.id}
                onChange={handleFAQChange(faq.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    flexDirection: isArabic ? 'row-reverse' : 'row',
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      transform: isArabic ? 'rotate(-90deg)' : 'rotate(0deg)',
                      '&.Mui-expanded': {
                        transform: isArabic ? 'rotate(0deg)' : 'rotate(180deg)',
                      }
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ textAlign: isArabic ? 'right' : 'left' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {faq.id === 'faq1' ? (
                    faq.answer
                  ) : (
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        textAlign: isArabic ? 'right' : 'left',
                        whiteSpace: 'pre-line',
                        lineHeight: 1.6
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          
          <Divider sx={{ my: 4 }} />

          {/* Terms of Service Section */}
          <Typography variant="h5" gutterBottom color="primary" sx={{ textAlign: isArabic ? 'right' : 'left' }}>
            {t('Terms & Conditions', 'الشروط والأحكام')}
          </Typography>
          
          <Box sx={{ 
            mt: 3, 
            direction: isArabic ? 'rtl' : 'ltr',
            textAlign: isArabic ? 'right' : 'left'
          }}>
            {!isArabic ? (
              // English Terms
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  1. Acceptance
                </Typography>
                <Typography variant="body2" paragraph>
                  By accessing or using Me'ar (the "Platform") you agree to these Terms & Conditions ("Terms"). If you do not agree, do not use the Platform.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  2. Definitions
                </Typography>
                <Typography variant="body2" paragraph>
                  "User Content" = any rating, comment, text, image or data you post.<br />
                  "Lecturer" = any individual reviewed on the Platform.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  3. Eligibility & Account
                </Typography>
                <Typography variant="body2" paragraph>
                  You must be at least 18 years old or have verifiable university enrolment and a valid Saudi mobile number or .edu email. You are responsible for all activity under your account.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  4. User Conduct
                </Typography>
                <Typography variant="body2" paragraph>
                  You agree NOT to:
                </Typography>
                <Box component="ul" sx={{ ml: 2, mt: 1 }}>
                  <Typography component="li" variant="body2">post false statements presented as fact;</Typography>
                  <Typography component="li" variant="body2">harass, threaten, bully or incite violence;</Typography>
                  <Typography component="li" variant="body2">upload content that is obscene, defamatory, discriminatory, or otherwise illegal under Saudi law;</Typography>
                  <Typography component="li" variant="body2">use the Platform for any unlawful, deceptive or commercial purpose;</Typography>
                  <Typography component="li" variant="body2">attempt to interfere with the Platform, its security or other users.</Typography>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  5. No Illicit Use / Anti-Cyber-Bullying Policy
                </Typography>
                <Typography variant="body2" paragraph>
                  Me'ar is provided solely for lawful, constructive academic feedback. You may never use it to stalk, dox, blackmail, defame, or otherwise cyber-bully any person. Violations can result in immediate content removal, account suspension, data referral to regulators, and civil or criminal liability.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  6. Content Moderation and Takedown
                </Typography>
                <Typography variant="body2" paragraph>
                  We may remove or edit any User Content that, in our sole discretion, breaches these Terms or applicable law. Lecturers or universities may request review removal by emailing saudimear@gmail.com with evidence of defamation or privacy infringement. We aim to respond within 48 hours and resolve within 5 working days.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  7. Privacy & PDPL Compliance
                </Typography>
                <Typography variant="body2" paragraph>
                  Our Privacy Notice explains how we collect, use and store personal data in accordance with the Saudi Personal Data Protection Law. Key points:
                </Typography>
                <Box component="ul" sx={{ ml: 2, mt: 1 }}>
                  <Typography component="li" variant="body2">lawful basis: legitimate interest + consent for publication;</Typography>
                  <Typography component="li" variant="body2">you may delete your reviews at any time;</Typography>
                  <Typography component="li" variant="body2">data subject requests: saudimear@gmail.com.</Typography>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  10. Disclaimers
                </Typography>
                <Typography variant="body2" paragraph>
                  The Platform provides opinions, not facts. We do not guarantee accuracy or endorse any User Content. The Platform is provided "as is" without warranties.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  11. Limitation of Liability
                </Typography>
                <Typography variant="body2" paragraph>
                  To the maximum extent permitted by Saudi law, Me'ar will not be liable for indirect or consequential losses arising from use of the Platform.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  12. Indemnity
                </Typography>
                <Typography variant="body2" paragraph>
                  You agree to indemnify Me'ar and its officers against any claims arising out of your breach of these Terms or your User Content.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  13. Governing Law & Jurisdiction
                </Typography>
                <Typography variant="body2" paragraph>
                  These Terms are governed by the laws of the Kingdom of Saudi Arabia. The competent courts of Riyadh have exclusive jurisdiction.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  14. Changes to Terms
                </Typography>
                <Typography variant="body2" paragraph>
                  We may update these Terms at any time. Material changes take effect 7 days after notice via email or banner.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  15. Contact
                </Typography>
                <Typography variant="body2" paragraph>
                  Questions or complaints? saudimear@gmail.com | Riyadh, Saudi Arabia.
                </Typography>
              </Box>
            ) : (
              // Arabic Terms
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  1. القبول
                </Typography>
                <Typography variant="body2" paragraph>
                  من خلال الوصول إلى أو استخدام معيار ("المنصة") فإنك توافق على هذه الشروط والأحكام ("الشروط"). إذا كنت لا توافق، فلا تستخدم المنصة.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  2. التعريفات
                </Typography>
                <Typography variant="body2" paragraph>
                  "محتوى المستخدم" = أي تقييم أو تعليق أو نص أو صورة أو بيانات تنشرها.<br />
                  "المحاضر" = أي فرد يتم مراجعته على المنصة.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  3. الأهلية والحساب
                </Typography>
                <Typography variant="body2" paragraph>
                  يجب أن تكون بعمر 18 عاماً على الأقل أو لديك تسجيل جامعي قابل للتحقق ورقم جوال سعودي صالح أو بريد إلكتروني .edu. أنت مسؤول عن جميع الأنشطة تحت حسابك.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  4. سلوك المستخدم
                </Typography>
                <Typography variant="body2" paragraph>
                  توافق على عدم:
                </Typography>
                <Box component="ul" sx={{ mr: 2, mt: 1 }}>
                  <Typography component="li" variant="body2">نشر بيانات كاذبة مقدمة كحقائق؛</Typography>
                  <Typography component="li" variant="body2">المضايقة أو التهديد أو التنمر أو التحريض على العنف؛</Typography>
                  <Typography component="li" variant="body2">رفع محتوى فاحش أو تشهيري أو تمييزي أو غير قانوني بموجب القانون السعودي؛</Typography>
                  <Typography component="li" variant="body2">استخدام المنصة لأي غرض غير قانوني أو خادع أو تجاري؛</Typography>
                  <Typography component="li" variant="body2">محاولة التدخل في المنصة أو أمانها أو المستخدمين الآخرين.</Typography>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  5. حظر الاستخدام غير المشروع / سياسة مكافحة التنمّر الإلكتروني
                </Typography>
                <Typography variant="body2" paragraph>
                  يتم توفير معيار فقط للتعليقات الأكاديمية المشروعة والبناءة. لا يجوز لك استخدامها للمطاردة أو الكشف عن المعلومات الشخصية أو الابتزاز أو التشهير أو التنمر الإلكتروني بأي شكل آخر. يمكن أن تؤدي الانتهاكات إلى إزالة المحتوى فوراً، وتعليق الحساب، وإحالة البيانات إلى الجهات التنظيمية، والمسؤولية المدنية أو الجنائية.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  6. إدارة المحتوى والإزالة
                </Typography>
                <Typography variant="body2" paragraph>
                  يجوز لنا إزالة أو تعديل أي محتوى مستخدم يخالف، حسب تقديرنا المنفرد، هذه الشروط أو القانون المعمول به. يمكن للمحاضرين أو الجامعات طلب إزالة المراجعة عبر البريد الإلكتروني saudimear@gmail.com مع أدلة التشهير أو انتهاك الخصوصية. نهدف للرد خلال 48 ساعة والحل خلال 5 أيام عمل.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  7. الخصوصية والامتثال لقانون حماية البيانات الشخصية (PDPL)
                </Typography>
                <Typography variant="body2" paragraph>
                  إشعار الخصوصية الخاص بنا يوضح كيف نجمع ونستخدم ونخزن البيانات الشخصية وفقًا لقانون حماية البيانات الشخصية السعودي (PDPL). النقاط الرئيسية:
                </Typography>
                <Box component="ul" sx={{ mr: 2, mt: 1 }}>
                  <Typography component="li" variant="body2">الأساس القانوني: المصلحة المشروعة + الموافقة على النشر؛</Typography>
                  <Typography component="li" variant="body2">يمكنك حذف مراجعاتك في أي وقت؛</Typography>
                  <Typography component="li" variant="body2">طلبات أصحاب البيانات: saudimear@gmail.com.</Typography>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  10. إخلاء المسؤولية
                </Typography>
                <Typography variant="body2" paragraph>
                  توفر المنصة آراء وليس حقائق. لا نضمن الدقة أو نؤيد أي محتوى مستخدم. يتم توفير المنصة "كما هي" دون ضمانات.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  11. تحديد المسؤولية
                </Typography>
                <Typography variant="body2" paragraph>
                  إلى أقصى حد يسمح به القانون السعودي، لن تكون معيار مسؤولة عن الخسائر غير المباشرة أو التبعية الناشئة عن استخدام المنصة.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  12. التعويض
                </Typography>
                <Typography variant="body2" paragraph>
                  توافق على تعويض معيار ومسؤوليها ضد أي مطالبات ناشئة عن انتهاكك لهذه الشروط أو محتوى المستخدم الخاص بك.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  13. القانون الحاكم والاختصاص
                </Typography>
                <Typography variant="body2" paragraph>
                  تحكم هذه الشروط قوانين المملكة العربية السعودية. المحاكم المختصة في الرياض لها اختصاص حصري.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  14. تغييرات على الشروط
                </Typography>
                <Typography variant="body2" paragraph>
                  يجوز لنا تحديث هذه الشروط في أي وقت. التغييرات الجوهرية تدخل حيز التنفيذ بعد 7 أيام من الإشعار عبر البريد الإلكتروني أو البانر.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  15. الاتصال
                </Typography>
                <Typography variant="body2" paragraph>
                  أسئلة أو شكاوى؟ saudimear@gmail.com | الرياض، المملكة العربية السعودية.
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('Contact Us', 'اتصل بنا')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('Have questions or feedback? Email us!', 'لديك أسئلة أو ملاحظات؟ راسلنا!')}
            <br />
            saudimear@gmail.com
          </Typography>
          
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('Follow us on social media:', 'تابعنا على وسائل التواصل الاجتماعي:')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              component="a"
              href="https://www.instagram.com/mear.sa/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<InstagramIcon />}
              variant="outlined"
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                px: 3,
                py: 1,
                '& .MuiButton-startIcon': {
                  marginRight: isArabic ? 2 : 1,
                  marginLeft: isArabic ? 0 : -0.25
                },
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderColor: 'primary.main'
                }
              }}
            >
              @mear.sa
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AboutPage; 